import React, { useState } from 'react'
import Tooltip from 'rc-tooltip'
import _ from 'lodash'
import styled, { css, keyframes } from 'styled-components'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useBotMetadata } from '@features/serverBots'
import { getEnvironmentName } from '@features/serverBots/utils/getEnvironmentName'

import DropdownMenuList from '../../design-system/DropdownMenu/DropdownMenuList'
import { originalVersionFlowFetchErrorSelector } from '../../../features/serverBots/selectors'
import { useDropdownItems } from '../hooks/useDropdownItems'
import { useCloseMenuAfterScroll } from '../hooks/useCloseMenuAfterScroll'
import { useOpenBotId } from '../../../store/app'
import { useBotConfiguration } from '../../../features/serverBots'

interface SwitchButtonProps {
  isDevelopment: boolean
  onClick: () => void
  isOpen: boolean
}

const SwitchEnvironments = React.memo(() => {
  const config = useBotConfiguration()
  const dropdownMenuItems = useDropdownItems()
  const metadata = useBotMetadata(useOpenBotId())
  const [open, setOpen] = useState<boolean>(false)
  const error = useSelector(originalVersionFlowFetchErrorSelector)

  useCloseMenuAfterScroll(() => setOpen(false))

  if (error || !config) {
    return null
  }

  return (
    <Tooltip
      placement="bottom"
      align={{ offset: [0, 8], targetOffset: [0, '50%'] }}
      overlayClassName="tooltip-dropdown-menu"
      visible={open}
      overlay={<DropdownMenuList items={dropdownMenuItems} />}
      onVisibleChange={(open: boolean | undefined) => setOpen(!!open)}
      trigger={['click']}
    >
      <SwitchButton
        onClick={() => setOpen(!open)}
        isDevelopment={metadata?.environmentType === 'test'}
        isOpen={open}
      >
        <Title>{_.capitalize(getEnvironmentName(metadata)!)}</Title>
        <ChevronIcon>
          <FontAwesomeIcon icon={open ? 'chevron-up' : 'chevron-down'} />
        </ChevronIcon>
      </SwitchButton>
    </Tooltip>
  )
})

export default SwitchEnvironments

const switchButtonHover = keyframes`
  from { padding-top: 0}
  to { padding-top: 5px}
`

const switchButtonClose = keyframes`
  from { padding-top: 5px}
  to { padding-top: 0}
`

const SwitchButton = styled.div<SwitchButtonProps>`
  position: absolute;
  left: calc(50% - 65px);
  top: -1px;
  z-index: 43;
  background: ${({ isDevelopment, isOpen }) =>
    isDevelopment
      ? isOpen
        ? 'rgb(135, 140, 156)'
        : 'rgba(135, 140, 156, 0.8)'
      : isOpen
      ? 'rgb(224, 123, 29)'
      : 'rgba(224, 123, 29, 0.8)'};
  border: 1px solid;
  border-color: ${({ isDevelopment }) =>
    isDevelopment ? 'rgba(135, 140, 156, 0.8)' : 'rgba(224, 123, 29, 0.8)'};
  border-top: none;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  color: white;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 12px;
  min-width: 120px;
  cursor: pointer;
  animation: ${switchButtonClose} 600ms cubic-bezier(0.16, 1, 0.3, 1) 0ms forwards;
  ${({ isOpen }) =>
    isOpen &&
    css`
      animation: ${switchButtonHover} 600ms cubic-bezier(0.16, 1, 0.3, 1) 0ms forwards;
    `}
  &:hover {
    background: ${({ isDevelopment }) =>
      isDevelopment ? 'rgb(135, 140, 156)' : 'rgb(224, 123, 29)'};
    animation: ${switchButtonHover} 600ms cubic-bezier(0.16, 1, 0.3, 1) 0ms forwards;
  }
`

const Title = styled.span`
  min-width: 100px;
  text-align: center;
  display: inline-block;
`

const ChevronIcon = styled.span``
